// cSpell:ignore aangemaakte aanmaakte actie acties afgerekende afrekening Alles Andere andere bedrag Bedrag berekening Beschrijving bestonden Betaald betaald betaaldetails betaalflow betaalstatus Betaalstatus betaaluitnodiging Betaaluitnodiging betaaluitnodigingen betrokken bevestigde bevroren bovenop Categorie daarna daaronder deze dezelfde downloaden Eigenaar eigenaar elke forfait formule gebouw gebruiken gebruikt gelinkte getoonde Groep hetzelfde hier huurder Huurders huurders indeling invitable Invitable keer komt koppelvlak kost Kosten kosten naast niet openstaand opent opgebouwd opgeslagen Optionele Overzicht pand panden provisie regels rijen saldo Samenvatting Scenariolaan slaan sluiten tellen titel Totalen Tuinonderhoud uitnodiging verbruik verdeelt verschil Verschuldigd Versturen versturen verwijderen verzonden volgende volledige voor wint zelfde zoals zodat
/* eslint-disable no-nested-ternary */
// Detail van een opgeslagen (bevestigde) afrekening, in exact dezelfde
// indeling als de betaaldetails-drawer (PaymentRequest): Page met titel +
// kebab + metadata-regels + tabs, en daaronder resource-cards.
//
// Tab "Info": huurders (saldo + betaalstatus + acties), eigenaar, kosten en
// pand als ResourceList-rijen. Tab "Detail": dezelfde cards als de
// wizard-Samenvatting, opgebouwd uit de bevroren snapshot.
//
// Betaalstatus-link: bij het versturen slaan we het aangemaakte
// paymentOrderId op in de snapshot-actie van dat contract. De status komt
// daarna live uit de v2 payment requests (request → items → paymentOrder.id),
// hetzelfde koppelvlak dat de provisie-berekening gebruikt.

import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { isPerformingSubmitActionSelector } from "@rentiohq/shared-frontend/dist/forms/createPayment/schema.createPayment.actions";
import { IPaymentPrefill } from "@rentiohq/shared-frontend/dist/forms/createPayment/schema.createPayment.types";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  confirm,
  IConfirm,
} from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  Card,
  Cell,
  DisplayText,
  ESpacings,
  Grid,
  Icon,
  Lozenge,
  Page,
  ResourceList,
  ResourceListItem,
  Stack,
  TextStyle,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { IAction } from "@rentiohq/web-shared/dist/types";
import React from "react";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PaymentForm as PaymentFormContainer } from "scenes/Properties/scenes/Payments/scenes-v2/PaymentForm";
import styled from "styled-components";
import {
  buildingAddressOf,
  isBuildingSettlement,
} from "../../../../buildingSettlement.utils";
import { LoadFailedBanner } from "../../../../components/ListLoadFailed";
import { Money, MoneyWithVat, VatLine } from "../../../../components/Money";
import { OwnerGroupList } from "../../../../components/OwnerGroupList";
import { departureLeaseEnd } from "../../../../departure.utils";
import {
  ICorrectionsBySettlement,
  leaseEndCorrectedIn,
} from "../../../../departureCorrection.utils";
import {
  departureDiffsByCat,
  isSettlementParty,
  OwnerCostLine,
  OwnerDepartureDiff,
  ownerHasPart,
} from "../../../../engine";
import { unitAddressLabel } from "../../../../groupUnits.utils";
import {
  PAYMENT_REQUESTS_PAGING_SORT,
  useCanEditCosts,
  usePropertyOwners,
} from "../../../../hooks";
import {
  EInviteBlockReason,
  fetchLeaseOneOffOrders,
  findInviteBlocks,
  getInviteBlockedInfo,
  IInviteBlock,
} from "../../../../inviteGuard.utils";
import { todayIsoDay } from "../../../../localDay.utils";
import { fromCents, roundCents, toCents } from "../../../../money.utils";
import {
  groupSharesByOwner,
  IOwnerPayGroups,
} from "../../../../ownerPayGroups.utils";
import { ownerKeyShares, ownerPartKeysOf } from "../../../../ownerShare.utils";
import {
  getInvitePayer,
  getPayerName,
  joinRecipientNamesAmong,
} from "../../../../payer.utils";
import {
  settlementDisplayName,
  settlementKindOf,
  settlementStoredName,
} from "../../../../settlementName.utils";
import { getSettlementPaymentDescription } from "../../../../settlementPayment.utils";
import {
  getActionPaymentStatus,
  withoutWithdrawnLink,
} from "../../../../settlementPaymentStatus.utils";
import {
  categorySpecOf,
  departureSettlementCosts,
  frozenContractDates,
  frozenUnitLabel,
  normalizeGroupSettlementCosts,
  ownerPartKeyName,
  settledKeyName,
  withCategorySpecs,
  withFrozenFigures,
} from "../../../../settlementView.utils";
import {
  fetchStatementUrl,
  requestStatementRender,
  statementFilename,
} from "../../../../statementDocument";
import { IStatementAddresses } from "../../../../statementParties.utils";
import {
  ApportionmentKey,
  CATEGORY_LABELS_NL,
  ContractRef,
  Invoice,
  isDepartureSettlement,
  PerContractResult,
  Settlement,
  SettlementAction,
  SettlementSnapshot,
  isTenantPaidStatus,
} from "../../../../types";
import { useEndedPaymentOrderIds } from "../../../../useEndedPaymentOrderIds";
import { PaymentStepBanner } from "../PaymentStepBanner";
import { confirmInTurn } from "../postRunDialogs.utils";
import {
  getConfirmedWriteFailedTextKey,
  isPaymentStillOpenRefusal,
} from "../saveFailed.utils";
import {
  canInviteAllAtOnce,
  canInvitePerOwner,
  changedSettlementActions,
  IPayShare,
  linkCreatedOrders,
  MIN_PAYABLE_AMOUNT,
  withPaymentLinks,
  markedTenantActions,
} from "../SettlementForm.utils";
import { getNoCostsText, isWithoutCosts } from "../settlementWizard.utils";
import {
  buildStatementSnapshot,
  statementVariant,
} from "./buildStatementSnapshot";
import {
  COST_KIND_ORDER,
  CostKindBar,
  costKindLabel,
  costKindOfInvoice,
} from "./costKinds";
import { confirmedCostShareContext } from "./costShareCalculation.utils";
import { oneLeaseInfoTotals } from "./infoCostTotals.utils";
import {
  btwOf,
  DetailTableWrap,
  fmt,
  fmtDate,
  ownerUnitsNote,
  parentNote,
  PartyTitle,
  TotalsDivider,
} from "./shared";
import {
  AlreadySettledCard,
  CardTotals,
  costCountText,
  costVatTotals,
  departureLeaseEndText,
  ICardTotal,
  OwnerSummaryCard,
  SummaryCard,
  tenantCardCosts,
  tenantClosingTotals,
} from "./Step4Summary";
import {
  buildCatMeta,
  confirmedCardDistribution,
  tenantOwedInclVat,
} from "./summaryCards.utils";
import {
  BodyCell,
  HEADER_CONTENT_TYPE,
  SummaryTable,
  TColumnAlign,
} from "./summaryTable.styles";
import { AssignableProperty } from "./types";

interface ConfirmedViewProps {
  settlement: Settlement;
  invoices: Invoice[];
  // The live costs are still loading, or failed to load (then onRetryInvoices
  // fetches them again): a short cost list is then no deleted cost.
  isLoadingInvoices?: boolean;
  invoicesLoadFailed?: boolean;
  onRetryInvoices?: () => void;
  contracts: ContractRef[];
  keys: ApportionmentKey[];
  assignable: AssignableProperty[];
  // Records payment links and tenant marks: only the actions given, the
  // server keeps every other one as stored (N08).
  persistActions: (actions: SettlementAction[]) => Promise<void>;
  // The settlement as stored right now, read again (the view follows): an
  // invitation is only sent once that says the tenant has none yet.
  reloadSettlement: () => Promise<Settlement | undefined>;
  // Recipient languages for the statements; missing → the broker's language.
  statementLocales?: {
    ownerByKey: Record<string, string | undefined>;
    byContractId: Record<string, string | undefined>;
  };
  // The office and the recipients' addresses, for a statement rebuilt for
  // an older settlement (one confirmed before its statements were frozen).
  statementAddresses?: IStatementAddresses;
  // Optionele kebab-actie naast de titel (drawer: verwijderen + sluiten).
  onDelete?: () => void;
  // Shown from somewhere it is not managed (a group settlement opened on one
  // of its units), or its property is archived: no tenant actions, whoever
  // looks.
  readOnly?: boolean;
  // The departure corrections the settlements of its tree made, this one's
  // included (M28): a departure they corrected says where.
  treeCorrections?: ICorrectionsBySettlement[];
}

// A tenant to invite from here: who pays, on which pand and how much.
interface IInviteShare extends IPayShare {
  // The lease's tenants changed since confirming: only a payment form of
  // their own asks for the payer, and says why.
  hasPayersChanged: boolean;
}

// "Andere — Tuinonderhoud", the same way as on the summary cards.
const catLabel = (inv: Invoice) => {
  const spec = categorySpecOf(inv);
  return withCategorySpecs(
    CATEGORY_LABELS_NL[inv.category],
    spec ? [spec] : undefined,
  );
};

// The Huurders/Eigenaar rows above are ResourceList items whose amount sits
// left of a kebab-action column. The Kosten table has no such column, so we
// inset its amount column by the same width to line the amounts up vertically.
const KEBAB_INSET = "40px";
const CostAmountAlign = styled.div`
  th:last-child,
  td:last-child {
    padding-right: ${KEBAB_INSET};
  }
`;

// The Kosten totals: the Detail cards' totals block (Lewis, 2026-10-19),
// its amounts on the edge of the table's amount column.
const CostTotalsBlock = styled(TotalsDivider)`
  padding-right: ${KEBAB_INSET};
`;

// The Kosten table's columns: Categorie, Beschrijving, Bedrag, on the
// widths the table had before its kinds got a bar each (Lewis, 2026-10-19).
const COST_COLUMNS: { heading: string; width: string; align: TColumnAlign }[] =
  [
    { heading: "costs.field.category", width: "39%", align: "left" },
    { heading: "costs.field.description", width: "37%", align: "left" },
    { heading: "costs.field.amount", width: "24%", align: "right" },
  ];

export const ConfirmedView: React.FC<ConfirmedViewProps> = ({
  settlement,
  invoices,
  isLoadingInvoices = false,
  invoicesLoadFailed = false,
  onRetryInvoices,
  contracts,
  keys,
  assignable,
  persistActions,
  reloadSettlement,
  statementLocales,
  statementAddresses,
  onDelete,
  readOnly = false,
  treeCorrections,
}) => {
  // An owner who sees only their own part of a group settlement
  // (snapshot.viewerPart) only reads it.
  const canEdit =
    useCanEditCosts(settlement.groupId ?? settlement.unitId) &&
    !readOnly &&
    !settlement.snapshot?.viewerPart;
  const s = settlement;
  const { snapshot } = s;
  // A building settlement (2026-10-16) shows its sections and provision
  // slices; an older group settlement reads as it did.
  const isBuilding = isBuildingSettlement(s);
  const perContract = snapshot?.perContract ?? [];
  const landlordTotal = snapshot?.landlordTotal ?? 0;

  const reduxStore = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<"info" | "detail">("info");

  // ── Kosten uit de bevroren snapshot ────────────────────────────────────────
  // Read as the wizard settled them (a cost on the group counts as a group
  // cost), whichever screen passes the costs in, so their keys match the
  // frozen snapshot's.
  // A departure settlement also settled its share of the parent group's
  // costs (see ISettledDeparture).
  const departureGroupId =
    s.scope === "unit" && isDepartureSettlement(s)
      ? assignable.find(p => p.id === s.unitId)?.parentId
      : undefined;
  // Each cost with the figures the settlement froze (withFrozenFigures): a
  // cost edited since still reads as it was settled.
  const statementCosts = React.useMemo(() => {
    const ids = new Set(snapshot?.invoiceIds ?? []);
    return departureSettlementCosts(
      normalizeGroupSettlementCosts(
        invoices
          .filter(i => ids.has(i.id))
          .map(i => withFrozenFigures(i, snapshot)),
        { scope: s.scope, groupId: s.groupId },
      ),
      departureGroupId,
    );
  }, [invoices, snapshot, s.scope, s.groupId, departureGroupId]);

  // A departure settlement whose lease no longer ends on the day it settled
  // up to (see departureLeaseEnd).
  const departure = snapshot?.departure;
  const departureLeaseChange = departure
    ? departureLeaseEnd({
        until: departure.until,
        lease: contracts.find(c => c.id === departure.contractId),
      })
    : undefined;
  // A settlement since corrected that move (M28): it says where, instead of
  // promising that "de volgende afrekening" corrects it.
  const departureLease = departure
    ? contracts.find(c => c.id === departure.contractId)
    : undefined;
  const departureCorrected =
    departureLeaseChange && departureLease
      ? leaseEndCorrectedIn({
          departureSettlementId: s.id,
          leaseEnd: departureLease.endDate,
          bySettlement: treeCorrections ?? [],
        })
      : undefined;

  // The rental period a tenant was settled with, frozen into their statement.
  const contractDatesFor = (pc: PerContractResult) =>
    frozenContractDates({ snapshot, contractId: pc.contractId, contracts });
  // The tenants of this settlement. Older snapshots also froze a € 0 row for
  // a lease without tenancy in the period (a tenant who moved in later).
  const settledTenants = perContract.filter(pc =>
    isSettlementParty({
      result: pc,
      lease: contractDatesFor(pc),
      periods: [
        { from: s.periodFrom, to: s.periodTo },
        ...statementCosts.map(inv => ({
          from: inv.periodFrom,
          to: inv.periodTo,
        })),
      ],
    }),
  );

  // A settled cost can still be deleted afterwards. The frozen totals keep
  // counting it while the cost list resolves from the live costs, so the list
  // would silently be shorter than the amounts above it. Name the gap.
  const missingCostCount =
    isLoadingInvoices || invoicesLoadFailed
      ? 0
      : Math.max(
          0,
          (snapshot?.invoiceIds?.length ?? 0) - statementCosts.length,
        );

  // The key that split the owners' part of each consumption or
  // metering-company category, for the owner cards (see ownerPartKeyName).
  const ownerPartKeyByCat = React.useMemo(() => {
    const out: Record<string, string> = {};
    const equalKeyName = keys.find(k => k.isSystem && k.isEqual)?.name;
    for (const inv of statementCosts) {
      if (inv.scope !== "common") continue;
      const name = ownerPartKeyName({
        method: snapshot?.distributionMethod?.[inv.category],
        keyId: snapshot?.selectedKeyId?.[inv.category],
        keyNameOf: keyId =>
          settledKeyName({
            snapshot,
            keyId,
            category: inv.category,
            liveName: keys.find(k => k.id === keyId)?.name,
            deletedName: getLocalizedText("costs.keys.deleted_name"),
          }),
        equalKeyName,
      });
      if (name) out[inv.category] = name;
    }
    return out;
  }, [statementCosts, snapshot, keys]);

  // How each category was split, for the Detail tab's cards.
  const cardDistribution = React.useMemo(
    () =>
      confirmedCardDistribution({
        settlement: s,
        costs: statementCosts,
        keys,
        assignable,
        departureGroupId,
      }),
    [s, statementCosts, keys, assignable, departureGroupId],
  );

  const catMeta = React.useMemo(
    () => buildCatMeta(statementCosts),
    [statementCosts],
  );

  // The Info tab's costs per kind, common first, as the Detail tab's cards
  // group them (costKindOfInvoice); a kind without costs has no bar.
  const costKindSections = React.useMemo(
    () =>
      COST_KIND_ORDER.map(kind => ({
        kind,
        costs: statementCosts.filter(inv => costKindOfInvoice(inv) === kind),
      })).filter(section => section.costs.length > 0),
    [statementCosts],
  );

  // Totalen van de afgerekende kosten voor de "Kosten"-card in de sidebar.
  // Added up in cents, as the rows show them, so the totals agree with
  // the rows to the cent.
  const costTotals = React.useMemo(() => {
    const exclCents = statementCosts.reduce(
      (sum, i) => sum + toCents(i.amountExclVat),
      0,
    );
    const btwCents = statementCosts.reduce(
      (sum, i) => sum + toCents(btwOf(i)),
      0,
    );
    return {
      excl: fromCents(exclCents),
      btw: fromCents(btwCents),
      incl: fromCents(exclCents + btwCents),
    };
  }, [statementCosts]);
  // The frozen figures of the whole settlement, for the Detail cards'
  // readings and who carries the rest of each invoice.
  const shareContext = confirmedCostShareContext({
    settlement: s,
    catMeta,
    distribution: cardDistribution,
    leaseDatesOf: contractDatesFor,
    assignable,
  });

  // ── PDF download — mijnhuurprofiel/rentmatch-style ─────────────────────────
  // The renders are enqueued when the settlement is saved, so by the time the
  // user clicks "Overzicht downloaden" the PDF is usually already on S3 and
  // the click downloads immediately. If the render is still running (or the
  // settlement predates the warm-up), we enqueue + poll in the background and
  // download as soon as it's ready.
  const [pdfBusy, setPdfBusy] = React.useState<string | null>(null);

  const buildStatementFor = (params: {
    mode: "tenant" | "owner";
    contractId?: string;
    ownerKey?: string;
    title: string;
  }) => {
    const partyId =
      params.mode === "owner" ? params.ownerKey ?? "" : params.contractId;
    const filename = statementFilename(params.title);
    // The document as it was confirmed. Rebuilding it from today's costs,
    // keys and contracts could print other amounts than were billed.
    const frozen = snapshot?.statements?.[`${params.mode}:${partyId ?? ""}`];
    if (frozen) {
      // As frozen: the API gives it its number.
      const statement = frozen;
      return {
        statement,
        filename,
        variant: statementVariant(params.mode, partyId, statement),
        isFrozen: true,
      };
    }
    // Older settlements, confirmed before the documents were frozen.
    const contract =
      params.contractId != null
        ? contracts.find(c => c.id === params.contractId) ?? null
        : null;
    const pc =
      params.contractId != null
        ? perContract.find(x => x.contractId === params.contractId)
        : undefined;
    const statement = buildStatementSnapshot({
      mode: params.mode,
      settlement: s,
      contract,
      contracts,
      costs: statementCosts,
      keys,
      selectedKeyId: snapshot?.selectedKeyId ?? {},
      // The distribution frozen at confirm time, so the document reproduces the
      // approved Samenvatting even for verbruik-/bedrag-based categories.
      distribution: snapshot ?? undefined,
      assignable,
      groupUnitIds: snapshot?.groupUnits?.map(u => String(u.id)),
      unitArchivedOn: Object.fromEntries(
        (snapshot?.groupUnits ?? []).flatMap(u =>
          u.archivedOn ? [[String(u.id), u.archivedOn]] : [],
        ),
      ),
      provisionsExpected: pc?.provision ?? 0,
      breakdown: pc?.breakdown,
      breakdownVat: pc?.breakdownVat,
      correctionProvisionByKey: pc?.correctionProvisionByKey,
      extraProvisions: pc?.extraProvisions,
      provisionBySlice: pc?.provisionBySlice,
      provisionMonthsBySlice: pc?.provisionMonthsBySlice,
      kinds: pc?.kinds,
      fixedFeeKinds: pc?.fixedFeeKinds,
      forfaitIntervalsBySlice: pc?.forfaitIntervalsBySlice,
      vatByInvoice: pc?.vatByInvoice,
      costByInvoice: pc?.costByInvoice,
      isDeparture: isDepartureSettlement(s),
      isBuildingSettlement: isBuilding,
      landlordTotal,
      ownerResult: snapshot?.owners?.find(o => o.ownerKey === params.ownerKey),
      alreadySettled: snapshot?.alreadySettled,
      ownerName:
        params.ownerKey != null
          ? snapshot?.ownerNames?.[params.ownerKey]
          : undefined,
      office: statementAddresses?.office,
      recipientAddress:
        params.mode === "owner"
          ? statementAddresses?.ownerByKey[params.ownerKey ?? ""]
          : params.contractId != null
            ? statementAddresses?.byContractId[params.contractId]
            : undefined,
      locale:
        params.mode === "owner"
          ? statementLocales?.ownerByKey[params.ownerKey ?? ""]
          : params.contractId != null
            ? statementLocales?.byContractId[params.contractId]
            : undefined,
    });
    // Variant = which document this is; content-hashed so a data change (or a
    // fix in the statement math) always yields a fresh render instead of a
    // stale cached PDF.
    const variant = statementVariant(params.mode, partyId, statement);
    return { statement, filename, variant, isFrozen: false };
  };

  const downloadStatement = async (params: {
    mode: "tenant" | "owner";
    contractId?: string;
    ownerKey?: string;
    title: string;
  }) => {
    if (pdfBusy) return;
    const { statement, filename, variant, isFrozen } =
      buildStatementFor(params);
    setPdfBusy(params.title);
    // What the dialog says when the download fails: the timeout explains
    // itself, while a network or server error only has technical English.
    let failureKey = "settlement.confirmed.download_failed_info";
    try {
      let url = await fetchStatementUrl(s.id, variant);
      if (!url) {
        // Not rendered yet (older settlement or render still in flight):
        // enqueue and poll the key the API renders it under until the signed
        // URL appears.
        const renderedVariant = await requestStatementRender({
          settlementId: s.id,
          variant,
          statement,
          isFrozen,
        });
        // Rendered earlier under the API's key: no need to wait.
        url = await fetchStatementUrl(s.id, renderedVariant);
        for (let attempt = 0; attempt < 48 && !url; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, 2500));
          // eslint-disable-next-line no-await-in-loop
          url = await fetchStatementUrl(s.id, renderedVariant);
        }
      }
      if (!url) {
        failureKey = "settlement.confirmed.pdf_timeout";
        throw new Error(failureKey);
      }
      // Cross-origin S3 URLs ignore the <a download> filename, so download
      // from a same-origin blob to keep the friendly filename.
      const blob = await (await fetch(url)).blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      confirm({
        type: "warning",
        title: getLocalizedText("settlement.confirmed.download_failed"),
        info: getLocalizedText(failureKey),
        primaryActions: [],
      });
    } finally {
      setPdfBusy(null);
    }
  };

  // ── Betaaluitnodiging + status ─────────────────────────────────────────────
  // The open payment form: one tenant, or several at once. A fresh form id
  // per opening, since the form keeps its answers per id.
  const [payInvite, setPayInvite] = React.useState<{
    formId: number;
    shares: IInviteShare[];
  } | null>(null);
  // Checking with the server before the form opens. The ref also stops a
  // second click that lands before the re-render.
  const [isPreparingInvite, setIsPreparingInvite] = React.useState(false);
  const isPreparingInviteRef = React.useRef(false);
  // Order ids die al bestonden op het moment dat de betaalflow opent — het
  // verschil na sluiten is de order die deze uitnodiging aanmaakte.
  const knownOrderIdsRef = React.useRef<number[]>([]);

  // The snapshot as last stored. The prop only follows once the settlement is
  // fetched again, and a write built on an older copy would drop what this
  // view stored just before (a payment link).
  const storedSnapshotRef = React.useRef(snapshot);
  React.useEffect(() => {
    storedSnapshotRef.current = snapshot;
  }, [snapshot]);
  const storeSnapshot = async (next: SettlementSnapshot) => {
    // Only what changed travels: another tab may have stored something for
    // another tenant since this view read the settlement (N08).
    const changed = changedSettlementActions({
      stored: storedSnapshotRef.current?.actions,
      next: next.actions,
    });
    if (changed.length > 0) await persistActions(changed);
    storedSnapshotRef.current = next;
  };

  // Manual "Zet betaalstatus op betaald" per tenant.
  const manualPaidByContract = React.useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const a of snapshot?.actions ?? []) {
      out[a.contractId] = a.manualPaidAt;
    }
    return out;
  }, [snapshot?.actions]);

  // "Mark as refunded" per tenant: the day the office paid a refund
  // back outside Rentio. Only a refund action takes it (the API refuses it
  // on any other).
  const refundedByContract = React.useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const a of snapshot?.actions ?? []) {
      out[a.contractId] = a.refundedAt;
    }
    return out;
  }, [snapshot?.actions]);
  const refundContractIds = React.useMemo(
    () =>
      new Set(
        (snapshot?.actions ?? [])
          .filter(a => a.type === "refund")
          .map(a => a.contractId),
      ),
    [snapshot?.actions],
  );

  const linkedOrderIds = React.useMemo(
    () =>
      (snapshot?.actions ?? [])
        .map(a => a.paymentOrderId)
        .filter((v): v is number => v != null),
    [snapshot?.actions],
  );

  // Betaalstatus per gelinkte order, uit de v2 payment requests van de
  // betrokken panden (or-of-eq, zelfde pattern als de provisie-fetch).
  const propertyIds = React.useMemo(() => {
    const ids = new Set<number>();
    // The settled tenants' units are frozen: a contract deleted since still
    // has its payment on its unit.
    [...contracts.map(c => c.unitId), ...perContract.map(pc => pc.unitId)]
      .map(Number)
      .forEach(n => {
        if (n > 0) ids.add(n);
      });
    const own = Number(s.unitId);
    if (!Number.isNaN(own)) ids.add(own);
    return Array.from(ids);
  }, [contracts, perContract, s.unitId]);

  const requestFilter =
    propertyIds.length === 1
      ? { requestPropertyId: { eq: propertyIds[0] } }
      : propertyIds.length > 1
        ? { or: propertyIds.map(id => ({ requestPropertyId: { eq: id } })) }
        : { requestPropertyId: { eq: -1 } };

  const { items: paymentRequests } = paymentRequestHooks.useGetAll({
    shouldRefetch: linkedOrderIds.length > 0,
    query: { filter: requestFilter, sort: PAYMENT_REQUESTS_PAGING_SORT },
  });

  const statusByOrderId = React.useMemo(() => {
    const out: Record<number, EPaymentRequestStatus> = {};
    for (const r of paymentRequests ?? []) {
      for (const item of r.paymentRequestItems ?? []) {
        const oid = item.paymentOrder?.id;
        if (oid == null) continue;
        const isPaid = isTenantPaidStatus(r.status);
        // Betaald wint van elke andere status op dezelfde order.
        if (out[oid] == null || isPaid) out[oid] = r.status;
      }
    }
    return out;
  }, [paymentRequests]);

  const statusByRequestId = React.useMemo(() => {
    const out: Record<number, EPaymentRequestStatus> = {};
    for (const r of paymentRequests ?? []) {
      if (r.id != null) out[r.id] = r.status;
    }
    return out;
  }, [paymentRequests]);

  // The tenants' actions without the links of payments withdrawn under
  // Payments (see withoutWithdrawnLink): such a tenant is outstanding again,
  // with "send invitation" and "mark paid" back.
  const endedOrderIds = useEndedPaymentOrderIds({
    linkedOrderIds,
    requestsLoaded: paymentRequests !== undefined,
  });
  const toLiveAction = (action: SettlementAction) =>
    withoutWithdrawnLink({
      action,
      requestStatus: getActionPaymentStatus({
        action,
        statusMaps: {
          byOrderId: statusByOrderId,
          byRequestId: statusByRequestId,
        },
      }),
      endedOrderIds,
    });
  const liveActions = React.useMemo(
    () => (snapshot?.actions ?? []).map(toLiveAction),
    [snapshot?.actions, statusByRequestId, statusByOrderId, endedOrderIds],
  );

  const orderIdByContract = React.useMemo(() => {
    const out: Record<string, number | undefined> = {};
    for (const a of liveActions) {
      out[a.contractId] = a.paymentOrderId;
    }
    return out;
  }, [liveActions]);

  // Bulk creation shares one order between tenants; the tenant's own payment
  // request (when the snapshot pinned it) is the precise per-tenant handle.
  const requestIdByContract = React.useMemo(() => {
    const out: Record<string, number | undefined> = {};
    for (const a of liveActions) {
      out[a.contractId] = a.paymentRequestId;
    }
    return out;
  }, [liveActions]);

  // A change to the stored settlement that the server refused, with its
  // reason in the user's language; the view then shows what is stored
  // (persistActions reads it again either way).
  const showWriteFailed = (params: { titleKey: string; error: unknown }) =>
    confirm({
      type: "warning",
      title: getLocalizedText(params.titleKey),
      info: getLocalizedText(getConfirmedWriteFailedTextKey(params.error)),
      secondaryAction: {
        title: getLocalizedText("payment_request.button.ok"),
      },
    });

  // Marking a tenant whose invitation was withdrawn also drops that stale
  // link, so the manual mark is what counts (see isSettlementActionPaid).
  // Refused when this view is older than what is stored, e.g. an invitation
  // sent from another tab in the meantime: the dialog says so.
  const setTenantManualPaid = async (
    contractId: string,
    manualPaidAt: string | undefined,
  ) => {
    if (!snapshot) return;
    try {
      // This tenant's action alone: the other tenants' marks as this view
      // last read them never travel along (N08).
      await persistActions(
        markedTenantActions({
          actions: snapshot.actions,
          contractId,
          mark: {
            paymentOrderId: orderIdByContract[contractId],
            paymentRequestId: requestIdByContract[contractId],
            manualPaidAt,
          },
        }),
      );
    } catch (error) {
      showWriteFailed({
        titleKey: "settlement.manual_paid_failed.title",
        error,
      });
    }
  };

  // "Mark as refunded" stores today as the day the refund was paid back
  // outside Rentio, "Undo refund" clears it again; refused the same way as
  // the paid mark.
  const setTenantRefunded = async (
    contractId: string,
    refundedAt: string | undefined,
  ) => {
    if (!snapshot) return;
    try {
      await persistActions(
        markedTenantActions({
          actions: snapshot.actions,
          contractId,
          mark: { refundedAt },
        }),
      );
    } catch (error) {
      showWriteFailed({
        titleKey: "settlement.manual_paid_failed.title",
        error,
      });
    }
  };

  // Who the invitation goes to and on which pand: the payer frozen on
  // confirm while they are still on the lease (see getInvitePayer). Without
  // a payer the payment form asks for one, among the lease's own tenants and
  // parents. Undefined only when the pand is unknown.
  const payTargetFor = (
    pc: PerContractResult,
  ):
    | { propertyId: number; payerId?: number; hasPayersChanged: boolean }
    | undefined => {
    const contract = contracts.find(c => c.id === pc.contractId);
    const { payerId, hasPayersChanged } = getInvitePayer({
      frozenPayerId: pc.payerAccountId,
      liveContract: contract,
    });
    const propertyId = Number(pc.unitId || contract?.unitId || s.unitId || 0);
    return propertyId > 0
      ? { propertyId, payerId, hasPayersChanged }
      : undefined;
  };

  // ── Header (titel + metadata + tabs + kebab), zoals de betaaldetails ───────
  const propId = s.unitId ?? s.groupId;
  const prop = assignable.find(p => p.id === propId);
  // An owner of one unit in the group cannot open the group itself: name it
  // (by its label frozen on confirm, else "Groep") rather than showing its
  // id, and do not link to it.
  const propLabel =
    prop?.address ??
    prop?.code ??
    snapshot?.propertyLabel ??
    (s.groupId ? getLocalizedText("costs.scope.group") : propId) ??
    "";
  const unitProp = assignable.find(p => p.id === s.unitId);
  // The settled unit's label as frozen on confirm (an archived unit is gone
  // from the live list); the live label for older snapshots.
  // The live address with its box spelled out ("Scenariolaan 1 bus 3, 9000
  // Gent", unitAddressLabel) when the unit is known.
  const unitAddress =
    (unitProp ? unitAddressLabel(unitProp) : undefined) ??
    (s.scope === "unit" ? snapshot?.propertyLabel : undefined) ??
    s.unitId ??
    getLocalizedText("system.property");
  const periodLabel = `${fmtDate(s.periodFrom)} → ${fmtDate(s.periodTo)}`;
  // A unit's full address with its box spelled out (unitAddressLabel), as
  // the costs and provisions steps name it; the label frozen on confirm for
  // a unit no longer in the live list.
  const unitLabelOf = (id: string) => {
    const p = assignable.find(a => String(a.id) === String(id));
    return p ? unitAddressLabel(p) : frozenUnitLabel(snapshot, id) ?? id;
  };
  // An owner's card title (Lewis, 2026-10-17): the unit's address for an
  // owner of one unit, the building's address and "Eigenaar van 4 panden"
  // under it for an owner of several, their addresses in its "i" (Lewis,
  // 2026-10-19). With what their units weigh of the building by the key
  // that split the owners' part, as frozen on confirm ("55/85 van het
  // gebouw").
  const ownerAddressOf = (
    unitIds: string[],
  ): { address?: string; note?: React.ReactNode } => {
    if (s.scope === "unit") return { address: unitAddress };
    const note = ownerUnitsNote({
      units: unitIds.map(unitId => ({ unitId, address: unitLabelOf(unitId) })),
      shares: ownerKeyShares({
        keys: ownerPartKeysOf(cardDistribution.byCat),
        building: cardDistribution,
        ownerUnitIds: unitIds,
        period: { from: s.periodFrom, to: s.periodTo },
      }),
    });
    if (unitIds.length === 1) {
      return { address: unitLabelOf(unitIds[0]), note };
    }
    return {
      address: buildingAddressOf(prop) ?? snapshot?.propertyLabel,
      note,
    };
  };
  // Full unit address (incl. bus number) for a contract's tenant. Group
  // settlements sit on different units, so resolve per contract.
  const addressForContract = (pc: PerContractResult): string =>
    s.scope === "unit" ? unitAddress : unitLabelOf(pc.unitId);
  // Plain-text form for the PDF file name and the "being generated" banner;
  // the cards render the same party-first order via PartyTitle.
  const tenantTitle = (pc: PerContractResult): string =>
    `${pc.tenantName} - ${addressForContract(pc)}`;
  // The owner part per owner. One party per owner of the units (frozen on
  // confirm); older snapshots have one owner card built from the totals.
  interface OwnerParty {
    key?: string;
    name: string;
    address?: string;
    baseByCat: Record<string, number>;
    vacancyByCat: Record<string, number>;
    vatByCat?: Record<string, number>;
    // Each departure difference on a row of its own (F85); absent on owners
    // frozen before they existed.
    departureDiffsByCat?: Record<string, OwnerDepartureDiff[]>;
    // Under the title of an owner of several units: how many, which ones
    // in its "i".
    note?: React.ReactNode;
    // The owner's units, for their share of a key (see OwnerSummaryCard).
    unitIds?: string[];
    // The owner's part per cost; absent on older snapshots.
    lines?: OwnerCostLine[];
    total: number;
    vat?: number;
  }
  const ownerParties: OwnerParty[] = snapshot?.owners?.length
    ? snapshot.owners
        // An owner whose departure difference cancels the rest still sees
        // the row that explains it.
        .filter(ownerHasPart)
        .map(o => ({
          key: o.ownerKey,
          name:
            snapshot.ownerNames?.[o.ownerKey] ||
            getLocalizedText("costs.split.owner"),
          ...ownerAddressOf(o.unitIds),
          unitIds: o.unitIds,
          baseByCat: o.baseByCat,
          vacancyByCat: o.vacancyByCat,
          vatByCat: o.vatByCat,
          departureDiffsByCat: departureDiffsByCat(o.lines),
          lines: o.lines,
          total: o.total,
          vat: o.vat,
        }))
    : landlordTotal > 0.01
      ? [
          {
            name: getLocalizedText("costs.split.owner"),
            address: s.scope === "unit" ? unitAddress : undefined,
            baseByCat: snapshot?.landlordBaseByCat ?? {},
            vacancyByCat: snapshot?.landlordVacancyByCat ?? {},
            vatByCat: snapshot?.landlordVatByCat,
            total: landlordTotal,
          },
        ]
      : [];
  const ownerTitleFor = (party: OwnerParty) =>
    party.address ? `${party.name} - ${party.address}` : party.name;

  // Under the title only the property: no description, not even the name a
  // settlement made before 2026-10-16 was given (Lewis, 2026-10-16).
  const metadata = (
    <div>
      <Spacer weight={ESpacerWeight.W08} />
      <Stack.Item>
        <Grid spacing="extraTight" alignItems="center">
          <Grid.Item>
            <Icon
              source={s.scope === "group" ? "buildings" : "house"}
              size="small"
            />
          </Grid.Item>
          <Grid.Item>
            {prop ? (
              <Button appearance="link" url={`/properties/${propId}`}>
                {propLabel}
              </Button>
            ) : (
              propLabel
            )}
          </Grid.Item>
        </Grid>
      </Stack.Item>

      <Spacer weight={ESpacerWeight.W08} />
      <Stack.Item>
        <Grid spacing="extraTight" alignItems="center">
          <Grid.Item>
            <Icon source="calendar" size="small" />
          </Grid.Item>
          <Grid.Item>
            {getLocalizedText("costs.field.period")}: {periodLabel}
          </Grid.Item>
        </Grid>
      </Stack.Item>

      <Spacer weight={ESpacerWeight.W08} />
      <Stack.Item>
        <Grid spacing="extraTight" alignItems="center">
          <Grid.Item>
            <Icon source="billStack" size="small" />
          </Grid.Item>
          <Grid.Item>
            {getLocalizedText("system.created_at", {
              date: fmtDate(s.confirmedAt ?? ""),
            })}
          </Grid.Item>
        </Grid>
      </Stack.Item>
    </div>
  );

  const tabs = [
    {
      content: getLocalizedText("settlement.confirmed.tab_info"),
      onClick: () => setTab("info"),
      isActive: tab === "info",
    },
    {
      content: getLocalizedText("settlement.confirmed.tab_detail"),
      onClick: () => setTab("detail"),
      isActive: tab === "detail",
    },
  ];

  const dropdown: IAction[] | undefined =
    onDelete && canEdit
      ? [
          {
            content: (
              <TextStyle variation="negative">
                {getLocalizedText("settlement.confirmed.delete")}
              </TextStyle>
            ),
            onClick: onDelete,
          },
        ]
      : undefined;

  const renderCardHeader = (heading: string) => (
    <DisplayText size="extraSmall" space="tight">
      {heading}
    </DisplayText>
  );

  // ── Info-tab: resource-rijen zoals de betaaldetails ────────────────────────

  // Verschuldigd bedrag incl. btw per huurder. De engine verdeelt kosten excl.
  // btw (pc.balance = excl-kost − provisie), dus we tellen de btw er hier
  // bovenop — zelfde formule als de Samenvatting — zodat het getoonde saldo én
  // de betaaluitnodiging het volledige bedrag incl. btw gebruiken.
  const tenantOwed = (pc: PerContractResult) => tenantOwedInclVat(pc, catMeta);

  // Owner share incl. VAT (structural + vacancy), from the frozen
  // snapshot-maps.
  const ownerOwed = () => {
    const base = snapshot?.landlordBaseByCat ?? {};
    const vac = snapshot?.landlordVacancyByCat ?? {};
    const exactVat = snapshot?.landlordVatByCat;
    let totExcl = 0;
    let totBtw = 0;
    for (const key of new Set([...Object.keys(base), ...Object.keys(vac)])) {
      const meta = catMeta[key];
      const rate = meta && meta.excl > 0 ? meta.btw / meta.excl : 0;
      const amt = (base[key] ?? 0) + (vac[key] ?? 0);
      totExcl += amt;
      // The engine's per-cost VAT, same as the owner card and the PDF; the
      // category average only for snapshots frozen before it existed.
      totBtw += exactVat?.[key] ?? amt * rate;
    }
    return { incl: +(totExcl + totBtw).toFixed(2), btw: +totBtw.toFixed(2) };
  };

  // Owner amount (excl. btw) with "+ € X btw" underneath — the same style as
  // the total row of the cost table.
  const balanceCell = (excl: number, btw: number): React.ReactNode => (
    <>
      <DisplayText size="medium" element="div" space="none">
        <Money amount={excl} element="div" />
      </DisplayText>
      <VatLine vat={btw} />
    </>
  );

  // A tenant's balance is one amount incl. VAT, as on the summary card and the
  // PDF: the provisions come off the total incl. VAT, so an excl. + btw split
  // of the balance cannot be traced back to anything. A refund shows unsigned;
  // the status next to the name says which way the money goes.
  const tenantBalanceCell = (incl: number): React.ReactNode => (
    <DisplayText size="medium" element="div" space="none">
      <Money amount={Math.abs(incl)} element="div" />
    </DisplayText>
  );

  // Same lozenge vocabulary + colors as the lists (green = betaald, blue =
  // verzonden, yellow = openstaand) so verzonden and niet-verzonden are
  // visually distinct at a glance. Non-actionable states stay subdued text.
  type TenantStatusVM =
    | {
        kind: "lozenge";
        appearance: "success" | "warning" | "info" | "default";
        message: string;
        tooltip?: string;
      }
    | { kind: "text"; message: string };
  const tenantStatus = (
    balance: number,
    contractId: string,
  ): TenantStatusVM => {
    if (balance < -0.005) {
      // Once the office marked the refund as paid back: green, with its day.
      const refundedAt = refundedByContract[contractId];
      if (refundedAt) {
        return {
          kind: "lozenge",
          appearance: "success",
          message: getLocalizedText("settlement.confirmed.refunded_on", {
            date: fmtDate(refundedAt),
          }),
        };
      }
      // Deliberate: Rentio never pays a tenant back. The broker settles a
      // negative balance outside Rentio (bank transfer / next rent).
      // A label like the other states, in blue (Lewis, 2026-10-17), with
      // the "how" on hover.
      return {
        kind: "lozenge",
        appearance: "info",
        message: getLocalizedText("settlement.confirmed.to_refund"),
        tooltip: getLocalizedText("settlement.confirmed.to_refund_tooltip"),
      };
    }
    if (balance <= 0.005) {
      return {
        kind: "lozenge",
        appearance: "default",
        message: getLocalizedText("costs.settlement.status.nothing_to_pay"),
      };
    }
    const orderId = orderIdByContract[contractId];
    const requestId = requestIdByContract[contractId];
    if (orderId == null && requestId == null) {
      // The manual per-tenant mark, for a tenant who paid outside Rentio. A
      // linked payment request decides instead: while it is open the tenant
      // has not paid (the server's delete guard reads it the same way).
      const manualPaidAt = manualPaidByContract[contractId];
      return manualPaidAt
        ? {
            kind: "lozenge",
            appearance: "success",
            message: getLocalizedText("costs.detail.paid_on", {
              date: fmtDate(manualPaidAt),
            }),
          }
        : {
            kind: "lozenge",
            appearance: "warning",
            message: getLocalizedText("settlement.confirmed.outstanding"),
          };
    }
    // The tenant's own request wins — bulk creation shares one order between
    // tenants, so the order-level status would mark everyone paid at once.
    const status =
      (requestId != null ? statusByRequestId[requestId] : undefined) ??
      (orderId != null ? statusByOrderId[orderId] : undefined);
    const paid = isTenantPaidStatus(status);
    return paid
      ? {
          kind: "lozenge",
          appearance: "success",
          message: getLocalizedText("costs.status.paid"),
        }
      : {
          kind: "lozenge",
          appearance: "info",
          message: getLocalizedText("costs.status.invitation_sent"),
        };
  };

  // ── Betaaluitnodiging versturen ────────────────────────────────────────────
  // A tenant who still owes and has no payment request yet. Not one marked as
  // paid outside Rentio: that would bill them a second time; the broker first
  // sets the status back to unpaid.
  const isInvitable = (pc: PerContractResult) =>
    canEdit &&
    tenantOwed(pc).incl > MIN_PAYABLE_AMOUNT &&
    orderIdByContract[pc.contractId] == null &&
    requestIdByContract[pc.contractId] == null &&
    !manualPaidByContract[pc.contractId];

  // What a tenant's invitation bills: the amount incl. VAT frozen on their
  // action when confirming (older snapshots: their balance), to the payer of
  // payTargetFor, described in the language of their statement (the
  // payer's).
  const inviteShareFor = (pc: PerContractResult): IInviteShare | undefined => {
    const target = payTargetFor(pc);
    if (!target) return undefined;
    const { contractId } = pc;
    const contract = contracts.find(c => c.id === contractId);
    const frozenAmount = (snapshot?.actions ?? []).find(
      action => action.contractId === contractId,
    )?.amount;
    return {
      contractId,
      tenantName: pc.tenantName,
      amount: +(frozenAmount ?? tenantOwed(pc).incl).toFixed(2),
      payerId: target.payerId,
      payerName: getPayerName({
        candidates: contract?.payerCandidates,
        accountId: target.payerId,
      }),
      hasPayersChanged: target.hasPayersChanged,
      propertyId: target.propertyId,
      unitLabel: s.scope === "group" ? addressForContract(pc) : undefined,
      description: getSettlementPaymentDescription({
        name: settlementStoredName(s),
        kind: settlementKindOf(s),
        period: { from: s.periodFrom, to: s.periodTo },
        locale:
          snapshot?.statements?.[`tenant:${contractId}`]?.locale ??
          statementLocales?.byContractId[contractId],
      }),
    };
  };

  const invitableShares = settledTenants
    .filter(isInvitable)
    .map(inviteShareFor)
    .filter((share): share is IInviteShare => share != null);
  // "Alle betaaluitnodigingen versturen" fills in the payment details once
  // for every tenant still to invite, see canInviteAllAtOnce. The owners are
  // only looked up when there is more than one such tenant.
  const inviteOwners = usePropertyOwners(
    invitableShares.length > 1
      ? invitableShares.map(share => share.propertyId)
      : [],
  );
  const canInviteAll = canInviteAllAtOnce({
    shares: invitableShares,
    ownerKeyByProperty: inviteOwners.keyByProperty,
    ownersLoaded: inviteOwners.loaded,
  });
  // Tenants of different owners: "Alle betaaluitnodigingen versturen per
  // eigenaar" opens one form per owner, one after the other, the beneficiary
  // chosen in each (see ownerPayGroups.utils). The tenants of a unit without
  // an owner keep their own "Betaaluitnodiging versturen".
  const inviteOwnerGroups = groupSharesByOwner({
    shares: invitableShares,
    propertyIdOf: share => share.propertyId,
    ownerKeyByProperty: inviteOwners.keyByProperty,
    nameByKey: inviteOwners.nameByKey,
  });
  const canInviteEachOwner =
    !canInviteAll &&
    canInvitePerOwner({
      shares: invitableShares,
      grouped: inviteOwnerGroups,
      ownersLoaded: inviteOwners.loaded,
    });
  // The owners' forms while they run: the owners, and whose form is open.
  const [inviteOwnerRun, setInviteOwnerRun] = React.useState<{
    grouped: IOwnerPayGroups<IInviteShare>;
    index: number;
  } | null>(null);

  // What stops these tenants from being invited, read from the server: the
  // settlement as stored now (the view follows) and their leases' payment
  // orders, see inviteGuard.utils. Throws when either cannot be read.
  const readInviteBlocks = async (
    shares: IInviteShare[],
  ): Promise<IInviteBlock[]> => {
    const [stored, orders] = await Promise.all([
      reloadSettlement(),
      fetchLeaseOneOffOrders(shares.map(share => share.contractId)),
    ]);
    if (!stored?.snapshot) {
      throw new Error("The settlement could not be read again");
    }
    // Every later write builds on what is stored now.
    storedSnapshotRef.current = stored.snapshot;
    const storedActions = stored.snapshot.actions ?? [];
    return findInviteBlocks({
      targets: shares,
      actions: storedActions.map(toLiveAction),
      linkedOrderIds: storedActions.flatMap(action =>
        action.paymentOrderId != null ? [Number(action.paymentOrderId)] : [],
      ),
      endedOrderIds,
      confirmedAt: stored.confirmedAt,
      orders,
    });
  };

  const showInviteCheckFailed = () =>
    confirm({
      type: "warning",
      title: getLocalizedText("settlement.invite_not_sent.title"),
      info: getLocalizedText("settlement.invite_check_failed"),
      secondaryAction: {
        title: getLocalizedText("payment_request.button.ok"),
      },
    });

  // Links the payment orders found for tenants whose link got lost, once
  // the broker says they belong to this settlement: the tenants then show
  // as invited.
  const linkFoundOrders = async (blocks: IInviteBlock[]) => {
    const { current } = storedSnapshotRef;
    if (!current) return;
    const orderByContract = Object.fromEntries(
      blocks.flatMap(block =>
        block.order ? [[block.contractId, block.order.id] as const] : [],
      ),
    );
    try {
      await storeSnapshot({
        ...current,
        actions: withPaymentLinks({
          actions: current.actions,
          links: { orderByContract, requestByContract: {} },
        }),
      });
    } catch (error) {
      showWriteFailed({
        titleKey: "settlement.payment_link_failed.title",
        error,
      });
    }
  };

  // Nothing was sent, and why. A payment order the settlement does not know
  // can be linked from here.
  const showInviteBlocked = (
    blocks: IInviteBlock[],
    shares: IInviteShare[],
  ) => {
    const unlinked = blocks.filter(
      block => block.reason === EInviteBlockReason.UnlinkedOrder,
    );
    confirm({
      type: "warning",
      title: getLocalizedText("settlement.invite_not_sent.title"),
      info: getInviteBlockedInfo({
        blocks,
        targets: shares,
        formatAmount: fmt,
        formatDay: fmtDate,
        extraKeys:
          shares.length > blocks.length
            ? ["settlement.invite_blocked.others_not_sent"]
            : [],
      }),
      primaryActions:
        unlinked.length > 0
          ? [
              {
                title: getLocalizedText("settlement.invite_blocked.link"),
                onPress: () => void linkFoundOrders(unlinked),
              },
            ]
          : undefined,
      secondaryAction: {
        title: getLocalizedText(
          unlinked.length > 0 ? "system.cancel" : "payment_request.button.ok",
        ),
      },
    });
  };

  // Opens the payment form for one tenant or for all of them at once, once
  // the server says none of them was invited in the meantime (another tab,
  // or a send whose link got lost). `ownerRun`: the form is one owner's
  // among the owners' forms.
  const openInvite = async (
    shares: IInviteShare[],
    ownerRun?: { grouped: IOwnerPayGroups<IInviteShare>; index: number },
  ) => {
    setInviteOwnerRun(null);
    if (shares.length === 0 || isPreparingInviteRef.current) return;
    isPreparingInviteRef.current = true;
    setIsPreparingInvite(true);
    try {
      let blocks: IInviteBlock[];
      try {
        blocks = await readInviteBlocks(shares);
      } catch {
        showInviteCheckFailed();
        return;
      }
      if (blocks.length > 0) {
        showInviteBlocked(blocks, shares);
        return;
      }
    } finally {
      isPreparingInviteRef.current = false;
      setIsPreparingInvite(false);
    }
    const state = (reduxStore.getState() as any)?.PAYMENT_ORDERS_V2;
    knownOrderIdsRef.current = state?.createPaymentOrders?.ids ?? [];
    setInviteOwnerRun(ownerRun ?? null);
    setPayInvite({ formId: generateFormId(), shares });
  };

  // Links the payment orders the form created to the tenants they bill:
  // the diff of the created order ids in redux since the form opened.
  // `isLinked` is false when created orders could not be linked; the
  // refusal and the orders then go into the dialog.
  const linkCreatedInvites = async (
    shares: IInviteShare[],
  ): Promise<{
    isLinked: boolean;
    error?: unknown;
    orderIds: number[];
    linkedShares: IInviteShare[];
  }> => {
    const state = (reduxStore.getState() as any)?.PAYMENT_ORDERS_V2;
    const ids: number[] = state?.createPaymentOrders?.ids ?? [];
    const createdOrderIds = ids.filter(
      id => !knownOrderIdsRef.current.includes(id),
    );
    const { current } = storedSnapshotRef;
    const linked = { orderIds: createdOrderIds, linkedShares: shares };
    if (!current) return { isLinked: true, ...linked };
    if (createdOrderIds.length === 0) return { isLinked: true, ...linked };
    knownOrderIdsRef.current = [
      ...knownOrderIdsRef.current,
      ...createdOrderIds,
    ];
    const links = linkCreatedOrders({
      shares,
      createdOrderIds,
      ordersById: state?.detail ?? {},
    });
    const linkedShares = shares.filter(
      share => links.orderByContract[share.contractId] != null,
    );
    try {
      await storeSnapshot({
        ...current,
        actions: withPaymentLinks({ actions: current.actions, links }),
      });
      return { isLinked: true, ...linked };
    } catch (error) {
      return {
        isLinked: false,
        error,
        orderIds: createdOrderIds,
        linkedShares: linkedShares.length > 0 ? linkedShares : shares,
      };
    }
  };

  // The orders exist but the settlement does not know them: say so, name
  // them, so the broker does not send them a second time from here. Refused
  // because an invitation of the tenant was already open (another tab), the
  // new order bills them twice: it has to be withdrawn.
  const paymentLinkFailedDialog = (params: {
    error?: unknown;
    orderIds: number[];
    linkedShares: IInviteShare[];
  }): IConfirm => {
    const { error, orderIds, linkedShares } = params;
    const values = {
      tenants: joinRecipientNamesAmong({
        shares: linkedShares,
        among: linkedShares,
      }),
      orders: orderIds.join(", "),
    };
    return {
      type: "warning",
      title: getLocalizedText("settlement.payment_link_failed.title"),
      info: isPaymentStillOpenRefusal(error)
        ? getLocalizedText("settlement.payment_link_failed.duplicate", values)
        : getLocalizedText("settlement.payment_link_failed.named", values),
      secondaryAction: {
        title: getLocalizedText("payment_request.button.ok"),
      },
    };
  };

  // The form checks with the server before it sends (checkBeforeSending)
  // and then creates the orders. Closing it meanwhile would lose them: they
  // are linked from the redux diff taken when it closes.
  const isCheckingInviteRef = React.useRef(false);
  const isInviteBusy = () =>
    isCheckingInviteRef.current ||
    isPerformingSubmitActionSelector(
      undefined,
      true,
    )(reduxStore.getState() as any);

  // Closed after sending, what the broker should know of it follows: a link
  // that failed; then the next owner's form, when the owners' forms run. Closed
  // without sending, it created no order, says nothing and ends them.
  const closeInvite = async () => {
    if (!payInvite || isInviteBusy()) return;
    const result = await linkCreatedInvites(payInvite.shares);
    setPayInvite(null);
    const isSent = result.orderIds.length > 0;
    const nextOwnerRun =
      isSent &&
      inviteOwnerRun &&
      inviteOwnerRun.index + 1 < inviteOwnerRun.grouped.groups.length
        ? { ...inviteOwnerRun, index: inviteOwnerRun.index + 1 }
        : undefined;
    setInviteOwnerRun(null);
    confirmInTurn(
      [result.isLinked ? undefined : paymentLinkFailedDialog(result)],
      {
        then: nextOwnerRun
          ? () =>
              void openInvite(
                nextOwnerRun.grouped.groups[nextOwnerRun.index].shares,
                nextOwnerRun,
              )
          : undefined,
      },
    );
  };

  // "Versturen" reads the server once more: the form may have stood open
  // while another tab sent the same invitation. Refused, nothing is sent:
  // the form closes and says why.
  const checkBeforeSending = async (): Promise<boolean> => {
    if (!payInvite) return false;
    const { shares } = payInvite;
    isCheckingInviteRef.current = true;
    let blocks: IInviteBlock[];
    try {
      blocks = await readInviteBlocks(shares);
    } catch {
      isCheckingInviteRef.current = false;
      showInviteCheckFailed();
      return false;
    }
    isCheckingInviteRef.current = false;
    if (blocks.length === 0) return true;
    await closeInvite();
    showInviteBlocked(blocks, shares);
    return false;
  };

  // A bulk submission creates the orders one tenant at a time. When a later one fails, the earlier ones exist anyway: link them,
  // name who was billed and close. The others stay outstanding, so sending
  // again only invites them. When nothing was created, the form shows the
  // error and the broker can simply retry.
  const handleInviteFailed = async () => {
    if (!payInvite || payInvite.shares.length < 2) return;
    const state = (reduxStore.getState() as any)?.PAYMENT_ORDERS_V2;
    const createdGroupCount: number =
      state?.createPaymentOrders?.createdGroupCount ?? 0;
    if (createdGroupCount === 0) return;
    const billed = payInvite.shares.slice(0, createdGroupCount);
    const missing = payInvite.shares.slice(createdGroupCount);
    const { isLinked } = await linkCreatedInvites(billed);
    setPayInvite(null);
    // The owners' forms stop here: the next owner waits for another click.
    setInviteOwnerRun(null);
    // Named after who the requests bill (the payer), not the lease's tenants.
    const partialFailure: IConfirm = {
      type: "warning",
      title: getLocalizedText("settlement.payments.partial_failure.title"),
      info: [
        getLocalizedText("settlement.confirmed.partial_failure_info", {
          billed: joinRecipientNamesAmong({
            shares: billed,
            among: payInvite.shares,
          }),
          missing: joinRecipientNamesAmong({
            shares: missing,
            among: payInvite.shares,
          }),
        }),
        ...(isLinked
          ? []
          : [getLocalizedText("settlement.payment_link_failed")]),
      ].join(" "),
    };
    confirmInTurn([partialFailure]);
  };

  // One tenant: their own payment, linked to their lease. Several: one
  // order and open payment per tenant, each on their own pand, lease and
  // language (the same splits as the wizard's "Alles in één
  // keer").
  const getInvitePrefill = (shares: IInviteShare[]): IPaymentPrefill => {
    const [first] = shares;
    const common: IPaymentPrefill = {
      description: first.description,
      vatPct: 0,
      costPayment: true,
      payerId: first.payerId,
      payerName: first.payerName,
    };
    if (shares.length === 1) {
      return {
        ...common,
        amount: first.amount,
        // The lease's tenants changed: the broker confirms the payer.
        askPayer: first.hasPayersChanged,
        contractId: first.contractId,
      };
    }
    return {
      ...common,
      amount: +shares.reduce((sum, share) => sum + share.amount, 0).toFixed(2),
      costSplits: shares.map(share => ({
        payerId: share.payerId,
        payerName: share.payerName,
        amount: share.amount,
        propertyId: share.propertyId,
        unitLabel: share.unitLabel,
        contractId: share.contractId,
        description: share.description,
      })),
    };
  };

  const renderInviteIntro = (shares: IInviteShare[]) => {
    const banners: React.ReactNode[] = [];
    // One owner's form among the owners' forms: which one, and who follows.
    if (inviteOwnerRun) {
      banners.push(
        <OwnerGroupList
          key="owners"
          grouped={inviteOwnerRun.grouped}
          currentRunIndex={inviteOwnerRun.index}
        />,
      );
    }
    // Several tenants: who pays what in one banner, the payment form's own
    // recap left out (hideCostRecap). A single tenant may choose the payer
    // in the form, so its summary's recap names who it is.
    if (shares.length > 1) {
      banners.push(<PaymentStepBanner key="bulk" shares={shares} />);
    }
    if (shares.some(share => share.hasPayersChanged)) {
      banners.push(
        <Banner key="payers" variation="warning" hasDismiss={false}>
          {getLocalizedText("settlement.confirmed.payers_changed")}
        </Banner>,
      );
    }
    // A VAT-taxable lease is billed like any lease here: the real costs with
    // the suppliers' VAT, no VAT of the lease (see leaseVat.utils).
    const vatTaxable = shares.filter(
      share => contracts.find(c => c.id === share.contractId)?.leaseChargesVat,
    );
    if (vatTaxable.length > 0) {
      banners.push(
        <Banner key="vat" variation="warning" hasDismiss={false}>
          {getLocalizedText("settlement.confirmed.vat_taxable_lease", {
            tenants: vatTaxable
              .map(share => share.payerName || share.tenantName)
              .join(", "),
          })}
        </Banner>,
      );
    }
    return banners.length > 0 ? (
      <div style={{ marginBottom: 16 }}>
        <Stack vertical spacing="tight">
          {banners}
        </Stack>
      </div>
    ) : undefined;
  };

  const renderTenantRow = (pc: PerContractResult) => {
    const owed = tenantOwed(pc);
    const invited =
      orderIdByContract[pc.contractId] != null ||
      requestIdByContract[pc.contractId] != null;
    const status = tenantStatus(owed.incl, pc.contractId);
    const title = tenantTitle(pc);
    const actions: IAction[] = [
      {
        content: getLocalizedText("settlement.summary.download_statement"),
        onClick: () =>
          void downloadStatement({
            mode: "tenant",
            contractId: pc.contractId,
            title,
          }),
      },
    ];
    const inviteShare = isInvitable(pc) ? inviteShareFor(pc) : undefined;
    if (inviteShare) {
      actions.push({
        content: getLocalizedText("settlement.confirmed.send_invitation"),
        onClick: () => void openInvite([inviteShare]),
      });
    }
    // Same shortcut as the costs detail: jump to the linked payment.
    const orderId = orderIdByContract[pc.contractId];
    if (orderId != null) {
      const contract = contracts.find(c => c.id === pc.contractId);
      const paymentPropertyId = pc.unitId || contract?.unitId || s.unitId;
      if (paymentPropertyId) {
        actions.push({
          content: getLocalizedText("costs.action.go_to_payment"),
          onClick: () =>
            navigate(
              `/properties/${paymentPropertyId}/payments?paymentOrderId=${orderId}`,
            ),
        });
      }
    }
    // Manual per-tenant paid toggle. Hidden when the real payment already says
    // paid; "niet-betaald" only undoes a manual override. Also hidden once a
    // betaaluitnodiging is sent (`invited`): marking it paid by hand would hide
    // that the invitation is still open — the payment must flow through it.
    const manualPaidAt = manualPaidByContract[pc.contractId];
    if (!canEdit) {
      // A user who may only read changes no payment state.
    } else if (manualPaidAt) {
      actions.push({
        content: getLocalizedText("settlement.confirmed.mark_unpaid"),
        onClick: () => void setTenantManualPaid(pc.contractId, undefined),
      });
    } else if (
      owed.incl > 0.005 &&
      !invited &&
      !(status.kind === "lozenge" && status.appearance === "success")
    ) {
      actions.push({
        content: getLocalizedText("settlement.confirmed.mark_paid"),
        onClick: () => void setTenantManualPaid(pc.contractId, todayIsoDay()),
      });
    }
    // A tenant who gets money back: Rentio never pays it, the office does
    // outside Rentio and marks it here as paid back today; the mark can be
    // undone.
    const refundedAt = refundedByContract[pc.contractId];
    if (canEdit && refundedAt) {
      actions.push({
        content: getLocalizedText("settlement.confirmed.undo_refund"),
        onClick: () => void setTenantRefunded(pc.contractId, undefined),
      });
    } else if (
      canEdit &&
      owed.incl < -0.005 &&
      refundContractIds.has(pc.contractId)
    ) {
      actions.push({
        content: getLocalizedText("settlement.confirmed.mark_refunded"),
        onClick: () => void setTenantRefunded(pc.contractId, todayIsoDay()),
      });
    }
    return (
      <ResourceListItem
        boxProps={{ py: ESpacings.tight }}
        item={pc}
        media={<Icon source="profile" />}
        mediaSize="medium"
        actions={actions}
      >
        {/* No wrapping: a long status (French) wraps inside the left column
            instead of pushing the amount onto a line of its own. */}
        <Grid
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
        >
          <Grid.Item flex="1 1 auto" style={{ minWidth: 0 }}>
            <Stack spacing="tight" alignment="center">
              <TextStyle variation="strong" element="div">
                {pc.tenantName}
              </TextStyle>
              {status.kind === "lozenge" && status.tooltip ? (
                <Tooltip tooltipContent={status.tooltip}>
                  <Lozenge appearance={status.appearance}>
                    {status.message}
                  </Lozenge>
                </Tooltip>
              ) : status.kind === "lozenge" ? (
                <Lozenge appearance={status.appearance}>
                  {status.message}
                </Lozenge>
              ) : (
                <TextStyle variation="subdued" size="small" element="div">
                  {status.message}
                </TextStyle>
              )}
            </Stack>
            {[
              s.scope === "group" ? addressForContract(pc) : undefined,
              parentNote(pc.parentNames),
            ]
              .filter(Boolean)
              .map(line => (
                <div key={line} style={{ marginTop: 2 }}>
                  <TextStyle variation="subdued" size="small" element="div">
                    {line}
                  </TextStyle>
                </div>
              ))}
          </Grid.Item>
          <Grid.Item flex="0 0 auto">{tenantBalanceCell(owed.incl)}</Grid.Item>
        </Grid>
      </ResourceListItem>
    );
  };

  const renderOwnerRow = (party: OwnerParty) => (
    <ResourceListItem
      boxProps={{ py: ESpacings.tight }}
      item={{ id: party.key ?? "owner" }}
      media={<Icon source="profile" />}
      mediaSize="medium"
      actions={[
        {
          content: getLocalizedText("settlement.summary.download_statement"),
          onClick: () =>
            void downloadStatement({
              mode: "owner",
              ownerKey: party.key,
              title: ownerTitleFor(party),
            }),
        },
      ]}
    >
      <Grid
        alignItems="center"
        justifyContent="space-between"
        flexWrap="nowrap"
      >
        <Grid.Item flex="1 1 auto" style={{ minWidth: 0 }}>
          <TextStyle variation="strong" element="div">
            {party.name}
          </TextStyle>
          <TextStyle variation="subdued" size="small" element="div">
            {party.address ??
              getLocalizedText("settlement.confirmed.fixed_and_vacancy")}
          </TextStyle>
        </Grid.Item>
        <Grid.Item flex="0 0 auto">
          {(() => {
            const owed =
              party.vat != null
                ? {
                    incl: +(party.total + party.vat).toFixed(2),
                    btw: party.vat,
                  }
                : ownerOwed();
            return balanceCell(owed.incl - owed.btw, owed.btw);
          })()}
        </Grid.Item>
      </Grid>
    </ResourceListItem>
  );

  // The cells in the style of the cost's split: the category with its
  // supplier under it, and the amount excl. VAT with its VAT under it. No
  // apportionment key or method here (Lewis, 2026-10-19): the Detail tab
  // says how each cost was split.
  const categoryCell = (inv: Invoice): React.ReactNode => (
    <div>
      {catLabel(inv)}
      {inv.supplier && (
        <div>
          <TextStyle variation="subdued" size="small">
            {inv.supplier}
          </TextStyle>
        </div>
      )}
    </div>
  );
  const amountCell = (excl: number, btw: number): React.ReactNode => (
    <MoneyWithVat amount={excl} vat={btw} />
  );

  // The costs first (Lewis, 2026-10-19), then the tenants and owners.
  const renderInfo = () => (
    <>
      <Card
        heading={renderCardHeader(
          s.scope === "unit"
            ? `${getLocalizedText(
                "settlement.confirmed.costs_heading",
              )} · ${unitAddress}`
            : getLocalizedText("settlement.confirmed.costs_heading"),
        )}
        space={ESpacings.base}
      >
        <DetailTableWrap>
          <CostAmountAlign>
            {/* Read-only cost overview of the frozen snapshot: rows are
                intentionally not clickable, nor are the Detail tab's
                (Lewis, 2026-10-19); a cost opens from the cost list.
                The costs of each kind under its bar, as the Detail tab's
                cards group them (Lewis, 2026-10-19): common first. */}
            <SummaryTable>
              <colgroup>
                {COST_COLUMNS.map(column => (
                  <col key={column.heading} style={{ width: column.width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {COST_COLUMNS.map(column => (
                    <Cell
                      header
                      key={column.heading}
                      content={getLocalizedText(column.heading)}
                      contentType={HEADER_CONTENT_TYPE[column.align]}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {statementCosts.length === 0 && (
                  <tr>
                    <BodyCell colSpan={COST_COLUMNS.length}>
                      <TextStyle variation="subdued">
                        {getLocalizedText("settlement.confirmed.no_costs")}
                      </TextStyle>
                    </BodyCell>
                  </tr>
                )}
                {costKindSections.map(section => (
                  <React.Fragment key={section.kind}>
                    <CostKindBar
                      kind={section.kind}
                      colSpan={COST_COLUMNS.length}
                    />
                    {section.costs.map(inv => (
                      <tr key={inv.id}>
                        <BodyCell>{categoryCell(inv)}</BodyCell>
                        <BodyCell>
                          {inv.description || "—"}
                          {/* A unit's own cost in a building: which unit,
                              by its full address. */}
                          {section.kind === "private" &&
                            s.scope !== "unit" &&
                            inv.unitId && (
                              <TextStyle
                                variation="subdued"
                                size="small"
                                element="div"
                              >
                                {unitLabelOf(inv.unitId)}
                              </TextStyle>
                            )}
                        </BodyCell>
                        <BodyCell align="right">
                          {amountCell(inv.amountExclVat, btwOf(inv))}
                        </BodyCell>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </SummaryTable>
          </CostAmountAlign>
        </DetailTableWrap>
        {statementCosts.length > 0 && (
          <CostTotalsBlock>
            <CardTotals totals={infoCostTotals} />
          </CostTotalsBlock>
        )}
      </Card>

      <Card
        heading={renderCardHeader(getLocalizedText("system.tenants"))}
        space={ESpacings.base}
        primaryActions={
          canInviteAll
            ? [
                {
                  content: getLocalizedText(
                    "settlement.confirmed.send_all_invitations",
                  ),
                  onClick: () => void openInvite(invitableShares),
                  isSubmitting: isPreparingInvite,
                },
              ]
            : canInviteEachOwner
              ? [
                  {
                    content: getLocalizedText(
                      "settlement.confirmed.send_all_invitations_per_owner",
                    ),
                    onClick: () =>
                      void openInvite(inviteOwnerGroups.groups[0].shares, {
                        grouped: inviteOwnerGroups,
                        index: 0,
                      }),
                    isSubmitting: isPreparingInvite,
                  },
                ]
              : undefined
        }
      >
        <ResourceList items={settledTenants} renderItem={renderTenantRow} />
      </Card>

      {ownerParties.length > 0 && (
        <Card
          heading={renderCardHeader(
            getLocalizedText(
              ownerParties.length > 1 ? "system.owners" : "costs.split.owner",
            ),
          )}
          space={ESpacings.base}
        >
          <ResourceList
            items={ownerParties.map(p => ({ ...p, id: p.key ?? "owner" }))}
            renderItem={renderOwnerRow}
          />
        </Card>
      )}
    </>
  );

  // ── Detail-tab: dezelfde cards als de wizard-Samenvatting ──────────────────
  const tenantLines = perContract.filter(
    pc => Math.abs(pc.cost) > 0.001 || Math.abs(pc.provision) > 0.001,
  );

  // The Info tab's Kosten totals, the Detail cards' block (Lewis,
  // 2026-10-19): the costs excl. VAT, their VAT per rate and incl. VAT,
  // added up from the rows above. With one lease (a unit's settlement, a
  // departure) the tenant's provisions and balance follow, as on their
  // card, but only when the tenant carries exactly these costs: otherwise
  // the lines would not add up. A building's settlement with several
  // tenants stops at the costs.
  const infoCostTotals: ICardTotal[] = (() => {
    const lines: ICardTotal[] = [
      ...costVatTotals({
        excl: costTotals.excl,
        vat: costTotals.btw,
        vatRows: statementCosts.map(inv => ({
          excl: roundCents(inv.amountExclVat),
          vat: btwOf(inv),
          rate: inv.vatPct,
        })),
      }),
      {
        id: "costs",
        label: getLocalizedText("settlement.summary.card.total_costs_incl"),
        info: {
          rows: costKindSections.map(section => ({
            label: costKindLabel(section.kind),
            value: costCountText(
              fromCents(
                section.costs.reduce(
                  (sum, inv) =>
                    sum + toCents(inv.amountExclVat) + toCents(btwOf(inv)),
                  0,
                ),
              ),
              section.costs.length,
            ),
          })),
        },
        amount: costTotals.incl,
      },
    ];
    const [onlyLease] = tenantLines;
    if (tenantLines.length !== 1) return lines;
    const tenantCosts = tenantCardCosts(onlyLease, catMeta);
    return oneLeaseInfoTotals({
      invoiceLines: lines,
      tenantCosts,
      closing: tenantClosingTotals({
        pc: onlyLease,
        costs: tenantCosts,
        lease: contracts.find(c => c.id === onlyLease.contractId),
        period: { from: s.periodFrom, to: s.periodTo },
        contract: contractDatesFor(onlyLease),
      }),
      isDeparture: isDepartureSettlement(s),
      invoicesLabel: getLocalizedText(
        "settlement.confirmed.total_invoices_incl",
      ),
      settledForLabel: getLocalizedText("settlement.confirmed.settled_for", {
        tenant: onlyLease.tenantName,
      }),
    });
  })();

  const renderDetail = () => (
    <Stack vertical spacing="loose">
      {tenantLines.map(pc => {
        const title = tenantTitle(pc);
        return (
          <SummaryCard
            key={pc.contractId}
            variant="confirmed"
            title={
              <PartyTitle
                name={pc.tenantName}
                address={addressForContract(pc)}
                note={parentNote(pc.parentNames)}
              />
            }
            period={{ from: s.periodFrom, to: s.periodTo }}
            pc={pc}
            catMeta={catMeta}
            contract={contractDatesFor(pc)}
            lease={contracts.find(c => c.id === pc.contractId)}
            distribution={cardDistribution}
            shareContext={shareContext}
            onOpenStatement={() =>
              void downloadStatement({
                mode: "tenant",
                contractId: pc.contractId,
                title,
              })
            }
          />
        );
      })}
      {/* What departure settlements already settled (frozen on confirm),
          only when there is any: the Stack spaces an empty child too. */}
      {(snapshot?.alreadySettled?.length ?? 0) > 0 && (
        <AlreadySettledCard
          lines={snapshot?.alreadySettled ?? []}
          contracts={contracts}
          unitLabelOf={unitLabelOf}
          confirmedCosts={statementCosts}
          corrections={treeCorrections}
          ownId={s.id}
        />
      )}
      {ownerParties.map(party => (
        <OwnerSummaryCard
          key={party.key ?? "owner"}
          variant="confirmed"
          title={
            <PartyTitle
              name={party.name}
              address={party.address}
              note={party.note}
            />
          }
          baseByCat={party.baseByCat}
          vacancyByCat={party.vacancyByCat}
          pctByCat={snapshot?.landlordPctByCat ?? {}}
          vatByCat={party.vatByCat}
          catMeta={catMeta}
          ownerPartKeyByCat={ownerPartKeyByCat}
          departureDiffsByCat={party.departureDiffsByCat}
          distribution={cardDistribution}
          unitIds={party.unitIds}
          lines={party.lines}
          shareContext={shareContext}
          departureCorrections={snapshot?.departureCorrections}
          onOpenStatement={() =>
            void downloadStatement({
              mode: "owner",
              ownerKey: party.key,
              title: ownerTitleFor(party),
            })
          }
        />
      ))}
    </Stack>
  );

  return (
    <>
      <Page
        title={settlementDisplayName(s)}
        metadata={metadata}
        tabs={tabs}
        dropdown={dropdown}
        setDocumentTitle={false}
      >
        {/* Confirmed without a single cost: every provision came back. */}
        {isWithoutCosts(snapshot) && (
          <>
            <Banner variation="info" hasDismiss={false}>
              {getNoCostsText({
                on: "summary",
                // A departure names its one tenant (M44).
                departureTenant: departure?.tenantName,
              })}
            </Banner>
            <Spacer weight={ESpacerWeight.W16} />
          </>
        )}
        {pdfBusy && (
          <div style={{ marginBottom: 12 }}>
            <Banner variation="info" hasDismiss={false}>
              {getLocalizedText("settlement.confirmed.pdf_busy", {
                title: pdfBusy,
              })}
            </Banner>
          </div>
        )}
        {invoicesLoadFailed && (
          <>
            <LoadFailedBanner
              title={getLocalizedText("costs.overview.load_failed_costs")}
              info={getLocalizedText("settlement.confirmed.costs_load_failed")}
              onRetry={onRetryInvoices}
            />
            <Spacer weight={ESpacerWeight.W16} />
          </>
        )}
        {departure && departureLeaseChange && (
          <>
            <Banner
              title={getLocalizedText("settlement.departure.lease_end_title")}
              variation={
                departureLeaseChange.kind === "earlier" && !departureCorrected
                  ? "warning"
                  : "info"
              }
              hasDismiss={false}
            >
              {departureCorrected
                ? getLocalizedText("settlement.departure.lease_end_corrected", {
                    tenant: departure.tenantName,
                    until: fmtDate(departure.until),
                    settlements: departureCorrected.settlementNames.join(", "),
                    amount: fmt(
                      fromCents(
                        toCents(departureCorrected.amount) +
                          toCents(departureCorrected.vat),
                      ),
                    ),
                  })
                : departureLeaseEndText({
                    change: departureLeaseChange,
                    tenant: departure.tenantName,
                    until: departure.until,
                    on: "departure",
                  })}
            </Banner>
            <Spacer weight={ESpacerWeight.W16} />
          </>
        )}
        {missingCostCount > 0 && (
          <>
            <Banner
              title={getLocalizedText(
                "settlement.confirmed.missing_costs_title",
              )}
              variation="warning"
              hasDismiss={false}
            >
              {getLocalizedText(
                missingCostCount === 1
                  ? "settlement.confirmed.missing_costs_one"
                  : "settlement.confirmed.missing_costs_many",
                { count: String(missingCostCount) },
              )}
            </Banner>
            <Spacer weight={ESpacerWeight.W16} />
          </>
        )}
        {tab === "info" && renderInfo()}
        {tab === "detail" && renderDetail()}
      </Page>

      {payInvite && (
        <PaymentFormContainer
          key={payInvite.formId}
          asModal
          formId={payInvite.formId}
          propertyIdOverride={payInvite.shares[0].propertyId}
          prefillOverride={getInvitePrefill(payInvite.shares)}
          intro={renderInviteIntro(payInvite.shares)}
          hideCostRecap={payInvite.shares.length > 1}
          onError={() => void handleInviteFailed()}
          beforeSubmit={checkBeforeSending}
          onClose={() => void closeInvite()}
        />
      )}
    </>
  );
};
